'use client';

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Howl } from 'howler'

// Components
import GameBoard from './GameBoard/GameBoard';
import GameOver, { GameOverRef } from './GameOver/GameOver';
import GameInfoModal from './GameInfoModal/GameInfoModal';
import GameHeader from './GameHeader/GameHeader';
import { CursorFollower } from '@components';

// GSAP
import { gsap } from 'gsap';

// Config
import { ANIMATION, CANVAS_SIZE, USE_ICON_FOR_CORRECT, EMOJI, DEBUG_LOGS, DEBUG_BUTTONS } from './config/constants';

// Utils
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import {
  hasSeenInfoModal,
  setHasSeenInfoModal,
  saveCompletedGame,
  clearGameData,
  hasCompletedGame,
  getCompletedGame,
  getInProgressGame,
  saveInProgressGame,
  clearInProgressGame,
  type GameProgress,
  type GameInProgress
} from './config/gameLocalStorage';

// Styles
import styles from './Game.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

// Assets
import IconQuestionMark from '@/assets/svg/Game/icon-question-mark.svg';
import IconArrowDownCta from '@/assets/svg/arrow-down-thin.svg';

// Debug
import { useDebug } from "@/utils/useDebugLog";

// Utils
import { KeyboardSequence } from '@/utils/KeyboardSequence';

// Context
import { useCoverStory } from "@/providers/CoverStoryContext";
import { useSoundContext } from '@/providers/SoundContext';

// Effects
import { useCursorFollower } from './effects/useCursorFollower';
import { decryptCoverStoryAlbum, decryptCoverStoryImage } from '@/core/utils';

// ----------------------------------------

interface GameProps {
  title: string;
  options: CoverStoryOptions;
  gameData: CoverStoryGameData;
}

interface ConfigOption {
  name: string;
  imageSrc: string;
  gridSize: number;
  canvasSize: number;
}

// -------------------- Component

const Game: React.FC<GameProps> = ({ title,options, gameData }) => {
  const { log } = useDebug({ name: 'Game', bgColor: '#9FC131', enabled: DEBUG_LOGS });

  // Get game state from context (single source of truth)
  const { gameState, setGameState } = useCoverStory();
  const { globalVolume } = useSoundContext();

  // SSR guards
  const { isReady: isViewSizeReady, isDesktop } = useViewSize();
  const { mounted } = useClientMount();

  // Check if user has already completed this game
  const gameNumber = gameData.gameNumber ? String(gameData.gameNumber) : '';
  const [albumImageBase64, setAlbumImageBase64] = useState<string>('');
  const [albumTitle, setAlbumTitle] = useState<string>('');

  // Check localStorage synchronously during initialization
  const completedGameData = useMemo(() => {
    if (typeof window === 'undefined' || !gameNumber) return null;
    return getCompletedGame(gameNumber);
  }, [gameNumber]);

  // Check for in-progress game data synchronously during initialization
  const inProgressGameData = useMemo(() => {
    if (typeof window === 'undefined' || !gameNumber || completedGameData) return null;
    return getInProgressGame(gameNumber);
  }, [gameNumber, completedGameData]);

  // -------------------- CMS data setup

  // CMS copy: Modal (memoized to prevent re-renders)
  const Copy_GameInfoModal = useMemo(() => ({
    eyebrow: options.howToModal?.eyebrow || 'Missing eyebrow',
    headline: options.howToModal?.headline || 'Missing headline',
    ctaButton: options.howToModal?.ctaLabel || 'Missing label',
    ariaLabel: options.ariaLabels?.playGame || 'Play game',
    stepOne: options.howToModal?.stepOne || { headline: 'Missing step one headline', subhead: 'Missing step one subhead' },
    stepTwo: options.howToModal?.stepTwo || { headline: 'Missing step two headline', subhead: 'Missing step two subhead' },
    stepThree: options.howToModal?.stepThree || { headline: 'Missing step three headline', subhead: 'Missing step three subhead' },
  }), [options]);

  // CMS copy: Input (memoized to prevent re-renders)
  const Copy_GameInput = useMemo(() => ({
    placeholderGuessAlbum: options.searchPlacholder || 'Missing placeholder…',
    clearButtonAriaLabel: options.ariaLabels?.clearInput || 'Missing clear input',
    submitButtonAriaLabel: options.ariaLabels?.submitInput || 'Missing submit input',
    incorrectGuess: options.incorrectGuess || 'Missing incorrect',
  }), [options]);

  // CMS copy: Meter (memoized to prevent re-renders)
  const Copy_GameMeter = useMemo(() => ({
    singular: options.tileCount?.singular || '{{count}} Missing tile copy',
    plural: options.tileCount?.plural || '{{count}} Missing tile copy',
  }), [options]);

  // CMS copy: Final Guess Toast (memoized to prevent re-renders)
  const Copy_FinalGuessToast = useMemo(() => ({
    message: options.finalGuess || "You're out of tiles. Time to make a final guess.",
  }), [options]);

  // CMS copy: Game Over (memoized to prevent re-renders)
  const Copy_GameOver = useMemo(() => ({
    headlineSingular: options.coverStoryEndScreenHeadline?.singular || 'You got it in {{count}} tile!',
    headlinePlural: options.coverStoryEndScreenHeadline?.plural || 'You got it in {{count}} tiles!',
    incorrectHeadline: options.coverStoryEndScreenIncorrect?.headline || 'Missing incorrect headline',
    incorrectSubhead: options.coverStoryEndScreenIncorrect?.subhead || 'Missing incorrect subhead',
    tileText: options.coverStoryEndScreenTileText || {},
    shareButton: options.coverStoryEndScreenButtonLabels?.share || 'Share',
    playMoreButton: options.coverStoryEndScreenButtonLabels?.playMore || 'Play More',
    shopButton: options.coverStoryEndScreenButtonLabels?.shop || 'Shop',
    shopUrl: gameData.coverStoryVinylShopLink || undefined,
    copiedToast: options.coverStoryEndScreenButtonLabels?.copied || 'Copied to clipboard',
    circleCta: options.coverStoryEndScreenButtonLabels?.cursorText || 'THE STORY',
  }), [options, gameData.coverStoryVinylShopLink]);

  // Parse grid size from CMS
  const parseGridSize = (gridSizeString: string): number => {
    // Note: "4-by-4" -> 4, "12-by-12" -> 12, etc.
    const match = gridSizeString.match(/\d+/);
    return match ? parseInt(match[0], 10) : 6;
  };

  // Get grid size from CMS or use default of 6
  let gridSize = 6;
  if (options.coverStoryGridSize?.[0]) {
    gridSize = parseGridSize(options.coverStoryGridSize[0]);
  }

  // Create config based on grid size from CMS
  const config: ConfigOption = {
    name: `${gridSize}x${gridSize}`,
    imageSrc: albumImageBase64,
    gridSize: gridSize,
    canvasSize: CANVAS_SIZE,
  };

  // -------------------- State

  const [gameStats, setGameStats] = useState(() => {
    if (completedGameData) {
      // Use saved tilesRevealed, fallback to calculating from emojiSet for backwards compatibility
      const tilesRevealed = completedGameData.tilesRevealed ?? completedGameData.emojiSet.split('').filter(e => e === EMOJI.TILE_REVEALED).length;
      return {
        tilesRevealed,
        totalTiles: gridSize * gridSize,
        progress: (tilesRevealed / (gridSize * gridSize)) * 100,
        remaining: (gridSize * gridSize) - tilesRevealed,
      };
    }
    return {
      tilesRevealed: 0,
      totalTiles: 36,
      progress: 0,
      remaining: 36,
    };
  });

  const [guessStats, setGuessStats] = useState(() => {
    if (completedGameData) {
      return {
        guessesUsed: completedGameData.guessCount,
        totalGuesses: 10,
        remaining: 10 - completedGameData.guessCount,
      };
    }
    if (inProgressGameData) {
      return {
        guessesUsed: inProgressGameData.guessesUsed,
        totalGuesses: 10,
        remaining: 10 - inProgressGameData.guessesUsed,
      };
    }
    return {
      guessesUsed: 0,
      totalGuesses: 10,
      remaining: 10,
    };
  });

  const [showGameOver, setShowGameOver] = useState(!!completedGameData);
  const [showInfoModal, setShowInfoModal] = useState(() => {
    // Check localStorage to see if user has seen the modal before
    return !hasSeenInfoModal();
  });

  const [restoredGuessesUsed, setRestoredGuessesUsed] = useState(() => {
    if (inProgressGameData) {
      return inProgressGameData.guessesUsed;
    }
    return 0;
  });

  const [restoredIncorrectGuesses, setRestoredIncorrectGuesses] = useState<number[]>(() => {
    if (inProgressGameData) {
      // Calculate incorrect guesses from meter data
      const incorrectGuessNumbers: number[] = [];
      inProgressGameData.guessMeterData.forEach((meterType, index) => {
        if (meterType === 'incorrect') {
          incorrectGuessNumbers.push(index + 1); // Guess numbers are 1-based
        }
      });
      return incorrectGuessNumbers;
    }
    return [];
  });

  const [guessMeterData, setGuessMeterData] = useState<Array<'tile' | 'incorrect'>>(() => {
    if (completedGameData) {
      // Only parse emojis up to the guessCount (exclude unused squares)
      // Use Array.from() to properly split Unicode emojis
      const emojis = Array.from(completedGameData.emojiSet).slice(0, completedGameData.guessCount);
      return emojis.map(emoji => {
        return emoji === EMOJI.TILE_REVEALED ? 'tile' : 'incorrect';
      });
    }
    if (inProgressGameData) {
      return [...inProgressGameData.guessMeterData];
    }
    return [];
  });

  // Keep ref in sync with state
  useEffect(() => {
    guessMeterDataRef.current = guessMeterData;
  }, [guessMeterData]);

  const [gameOverCopyData, setGameOverCopyData] = useState<{
    headline: string;
    subhead: string;
    body: string | null;
    shareButton: string;
    playMoreButton: string;
    shopButton: string;
    shopUrl: string;
    copiedToast: string;
    circleCta?: string;
  } | null>(null);

  // -------------------- Refs

  // Track all tile IDs and which are unflipped
  const allTileIdsRef = useRef<number[]>([]);
  const unflippedTileIdsRef = useRef<number[]>([]);
  const gameOverTriggeredRef = useRef(false);
  const initialStateSetRef = useRef(false);
  const audioHowlRef = useRef<Howl | null>(null);
  const audioAutoFadeOutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringStateRef = useRef(false); // Track if we're currently restoring saved state
  const saveInProgressStateRef = useRef<(() => void) | null>(null); // Store save function to avoid circular deps

  // Refs to track current state values for saving
  const guessMeterDataRef = useRef<Array<'tile' | 'incorrect'>>([]);
  const guessStatsRef = useRef({ guessesUsed: 0, totalGuesses: 10, remaining: 10 });
  const gameStatsRef = useRef({ tilesRevealed: 0, totalTiles: 0, progress: 0, remaining: 0 });

  // Element refs
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapperGameBoardRef = useRef<HTMLDivElement>(null);
  const gameFooterRef = useRef<HTMLDivElement>(null);
  const wrapperGameOverRef = useRef<HTMLDivElement>(null);
  const gameMainContentRef = useRef<HTMLDivElement>(null);
  const gameOverRef = useRef<GameOverRef>(null);

  // GameBoard callbacks
  const getRemainingTilesRef = useRef<(() => number[]) | null>(null);
  const flipTileRef = useRef<((tileId: number) => void) | null>(null);
  const getTilesMapRef = useRef<(() => Map<number, { wrapper: HTMLDivElement }>) | null>(null);
  const wrapperGameInputRef = useRef<HTMLDivElement | null>(null);
  const wrapperGameMeterRef = useRef<HTMLDivElement | null>(null);
  const triggerGameOverRef = useRef<((reason: 'won' | 'out-of-guesses') => void) | null>(null);

  // -------------------- Game Over!

  const generateGameOverCopy = useCallback((won: boolean, guessesUsed: number) => {
    let tileRangeKey: string;
    if (guessesUsed === 0) {
      tileRangeKey = 'tiles0';
    } else if (guessesUsed === 1) {
      tileRangeKey = 'tiles1';
    } else if (guessesUsed >= 2 && guessesUsed <= 4) {
      tileRangeKey = 'tiles2Through4';
    } else if (guessesUsed >= 5 && guessesUsed <= 7) {
      tileRangeKey = 'tiles5Through7';
    } else {
      tileRangeKey = 'tiles8Through10';
    }

    let headlineTemplate = '';
    let subhead = '';
    let body: string | null = null;

    if (won) {
      headlineTemplate = guessesUsed === 1 ? Copy_GameOver.headlineSingular : Copy_GameOver.headlinePlural;
      const tileRangeCopy = Copy_GameOver.tileText?.[tileRangeKey as keyof typeof Copy_GameOver.tileText];

      if (Array.isArray(tileRangeCopy) && tileRangeCopy.length > 0) {
        const randomIndex = Math.floor(Math.random() * tileRangeCopy.length);
        const selectedItem = tileRangeCopy[randomIndex];
        if (selectedItem && typeof selectedItem === 'object' && 'subhead' in selectedItem) {
          subhead = selectedItem.subhead || '';
        }
      }
    } else {
      headlineTemplate = Copy_GameOver.incorrectHeadline;
      subhead = Copy_GameOver.incorrectSubhead;
    }

    const headline = headlineTemplate?.replace('{{count}}', String(guessesUsed)) || '';

    return {
      headline,
      subhead,
      body,
      shareButton: Copy_GameOver.shareButton,
      playMoreButton: Copy_GameOver.playMoreButton,
      shopButton: Copy_GameOver.shopButton,
      shopUrl: Copy_GameOver.shopUrl,
      copiedToast: Copy_GameOver.copiedToast,
      circleCta: Copy_GameOver.circleCta,
    };
  }, [Copy_GameOver]);

  const decryptGameData = useCallback(async () => {
    const image = await decryptCoverStoryImage(gameData.coverStoryAlbumImage || '');
    setAlbumImageBase64(image);
    const { album } = await decryptCoverStoryAlbum(gameData.albumFormatted) || {};
    setAlbumTitle(album);
  }, [gameData]);

  // -------------------- CursorFollower

  const {
    showCursorFollower,
    setShowCursorFollower,
    externalCursorPos,
    setExternalCursorPos,
    calculateCursorFollowerPosition,
  } = useCursorFollower({
    rootRef,
    enabled: true
  });

  // -------------------- Effects

  // Reset refs and game state on mount (handles component remounting via key prop)
  useEffect(() => {
    log('Component mounted - resetting refs and game state');
    gameOverTriggeredRef.current = false;
    initialStateSetRef.current = false;
    setGameState('playing');
  }, []);

  // Secret keyboard sequence to clear localStorage (type "99999")
  useEffect(() => {
    const keySeq = new KeyboardSequence({
      sequences: [
        {
          sequence: '99999',
          callback: () => {
            log('Secret code activated! Clearing localStorage.');
            localStorage.clear();
          },
          description: 'Clear localStorage and reload'
        }
      ],
      debug: false // DEBUG_LOGS
    });

    return () => keySeq.destroy();
  }, []);

  // Decrypt game data on mount
  useEffect(() => {
    decryptGameData();
  }, [gameData]);

  // Lock body scroll when info modal is open
  useEffect(() => {
    const body = document.body;

    if (showInfoModal) {
      body.classList.add('locked');
    } else {
      body.classList.remove('locked');
    }

    // Cleanup on unmount
    return () => {
      body.classList.remove('locked');
    };
  }, [showInfoModal]);

  // Game over
  const gameOverMan = useCallback((reason: 'won' | 'out-of-guesses', playerSubmitted?: boolean) => {

    const newGameState = reason === 'won' ? 'won' : 'lost';
    setGameState(newGameState);

    log('gameOverMan', { reason, newGameState, playerSubmitted });

    // Generate game over copy
    const copyData = generateGameOverCopy(newGameState === 'won', guessStats.guessesUsed);
    log('gameOverMan - Generated game over copy', copyData);
    setGameOverCopyData(copyData);

    // -------------------- Show GameOver component

    setShowGameOver(true);

    // --------------------

    // Get all tile IDs and organize by column (left to right)
    const allTileIds = allTileIdsRef.current;

    // Group tiles by column
    // Tile ID = row * gridSize + col, so col = tileId % gridSize
    const tilesByColumn: { [key: number]: number[] } = {};
    for (let i = 0; i < allTileIds.length; i++) {
      const tileId = allTileIds[i];
      const col = tileId % gridSize;
      if (!tilesByColumn[col]) {
        tilesByColumn[col] = [];
      }
      tilesByColumn[col].push(tileId);
    }

    // Get tiles map from GameBoard
    const tilesMap = getTilesMapRef.current?.();
    if (!tilesMap) {
      log('ERROR: tilesMap not available');
      return;
    }

    // Create timeline
    const tl = gsap.timeline({
      onComplete: () => {
        log('Timeline complete - all tiles faded out');
        if (wrapperGameBoardRef.current) {
          gsap.set(wrapperGameBoardRef.current, { pointerEvents: 'none', visibility: 'hidden' });
        }
        if (gameOverRef.current) {
          gameOverRef.current.animateIn();
        }

        // Show CursorFollower after GameOver animations
        if (Copy_GameOver.circleCta) {
          // Set position and show with a slight delay for GameOver animations
          setTimeout(() => {
            const pos = calculateCursorFollowerPosition();
            if (pos) {
              setExternalCursorPos(pos);
              setShowCursorFollower(true);
            }
          }, 1500); // Delay to let GameOver button row animate in first
        }
      }
    }).add('start');

    // -------------------- Fade out GameInput and GameMeter at the start

    // GameInput - out
    if (wrapperGameInputRef.current) {
      // log('Adding GameInput fade animation');
      tl.add('gameInputOut', 'start+=0.1');
      tl.to(wrapperGameInputRef.current, {
        autoAlpha: 0,
        duration: 0.4,
        ease: 'power1.out',
      }, 'gameInputOut');
    }

    // GameMeter - out
    if (wrapperGameMeterRef.current) {
      // log('Adding GameMeter fade animation');
      tl.add('gameMeterOut', 'start+=0.1');
      tl.to(wrapperGameMeterRef.current, {
        autoAlpha: 0,
        duration: 0.4,
        ease: 'power1.out',
      },'gameMeterOut');
    }

    // GameFooter - out
    if (gameFooterRef.current) {
      log('Adding GameFooter fade out');
      tl.add('gameFooterOut', 'start+=-0.1');
      tl.set(gameFooterRef.current, { pointerEvents: 'none' }, 'gameFooterOut');
      tl.to(gameFooterRef.current, {
        autoAlpha: 0,
        y: 24,
        duration: 0.4,
        ease: 'power1.out'
      }, 'gameFooterOut');
    }

    // -------------------- Fade out all tiles, column by column (left to right)

    const fadeDelay = ANIMATION.GAME_OVER_FADE_TILES_GAP_DUR; // Delay between columns
    const fadeDuration = ANIMATION.GAME_OVER_FADE_TILES_DUR; // Duration for each tile fade
    for (let col = 0; col < gridSize; col++) {
      const columnTiles = tilesByColumn[col] || [];

      // Fade all tiles in this column simultaneously
      columnTiles.forEach(tileId => {
        const tileElements = tilesMap.get(tileId);
        if (tileElements?.wrapper) {
          tl.to(tileElements.wrapper, {
            opacity: 0,
            duration: fadeDuration,
            ease: 'power1.out'
          }, col * fadeDelay); // Position at column's start time
        }
      });
    }
    if (playerSubmitted && audioHowlRef.current) {
      tl.call(() => {
        const fadeTime = 1000;
        audioHowlRef.current.play();
        audioHowlRef.current.fade(0, globalVolume, fadeTime);
        if (audioAutoFadeOutTimeoutRef.current) {
          clearTimeout(audioAutoFadeOutTimeoutRef.current);
        }
        audioAutoFadeOutTimeoutRef.current = setTimeout(() => {
          audioHowlRef.current.fade(globalVolume, 0, fadeTime);
        }, audioHowlRef.current.duration() * 1000 - fadeTime);
      }, [], ">=")
    }

  }, [setGameState, gridSize, guessStats.guessesUsed]);

  const fadeOutAndStopAudio = useCallback((fadeDuration = 300, unload = false) => {
    const howl = audioHowlRef.current;
    if (!howl) return;

    if (audioAutoFadeOutTimeoutRef.current) {
      clearTimeout(audioAutoFadeOutTimeoutRef.current);
      audioAutoFadeOutTimeoutRef.current = null;
    }

    if (audioStopTimeoutRef.current) {
      clearTimeout(audioStopTimeoutRef.current);
      audioStopTimeoutRef.current = null;
    }

    if (!howl.playing()) {
      howl.stop();
      if (unload) {
        howl.unload();
        audioHowlRef.current = null;
      }
      return;
    }

    const fromVolume = howl.volume();
    if (fromVolume > 0) {
      howl.fade(fromVolume, 0, fadeDuration);
      audioStopTimeoutRef.current = setTimeout(() => {
        howl.stop();
        if (unload) {
          howl.unload();
          if (audioHowlRef.current === howl) {
            audioHowlRef.current = null;
          }
        }
      }, fadeDuration);
      return;
    }

    howl.stop();
    if (unload) {
      howl.unload();
      audioHowlRef.current = null;
    }
  }, []);

  // -------------------- Restoration

  // Restore in-progress game from saved data (flip tiles only - state already initialized)
  const restoreInProgressGame = useCallback((savedData: GameInProgress) => {
    log('Restoring in-progress game - flipping tiles...', savedData);
    isRestoringStateRef.current = true;

    // Flip the saved tiles
    if (savedData.flippedTileIds.length > 0 && flipTileRef.current) {
      log('Restoring flipped tiles:', savedData.flippedTileIds);

      // Update unflipped tiles ref
      unflippedTileIdsRef.current = allTileIdsRef.current.filter(
        id => !savedData.flippedTileIds.includes(id)
      );

      // Flip each tile with a small stagger for visual effect
      savedData.flippedTileIds.forEach((tileId, index) => {
        setTimeout(() => {
          flipTileRef.current?.(tileId);

          // Mark restoration complete after last tile
          if (index === savedData.flippedTileIds.length - 1) {
            setTimeout(() => {
              isRestoringStateRef.current = false;
              log('Game state restoration complete');
            }, 200); // Small delay after last tile flip animation
          }
        }, index * 50); // 50ms stagger between tiles
      });
    } else {
      isRestoringStateRef.current = false;
      log('No tiles to restore, state restoration complete');
    }
  }, []);

  // -------------------- Handlers

  // Handle game over
  const handleGameOver = useCallback((reason: 'won' | 'out-of-guesses', playerSubmitted?: boolean) => {
    // Prevent duplicate execution
    if (gameOverTriggeredRef.current) {
      log('handleGameOver already triggered, ignoring duplicate call');
      return;
    }
    gameOverTriggeredRef.current = true;

    gameOverMan(reason, playerSubmitted);
  }, [gameOverMan]);

  // Save in-progress game state to localStorage
  const saveInProgressState = useCallback(() => {
    if (!gameNumber) return;
    if (gameState !== 'playing') return; // Don't save if game is over

    // Don't save if tile tracking hasn't been initialized yet
    if (allTileIdsRef.current.length === 0) {
      log('Skipping save - tile tracking not initialized yet');
      return;
    }

    // Get flipped tiles (all tiles minus unflipped ones)
    const flippedTileIds = allTileIdsRef.current.filter(
      id => !unflippedTileIdsRef.current.includes(id)
    );

    const inProgressData: GameInProgress = {
      gameNumber: String(gameNumber),
      gameTitle: title,
      gridSize: gridSize,
      flippedTileIds,
      guessMeterData: [...guessMeterDataRef.current],
      guessesUsed: guessStatsRef.current.guessesUsed,
      tilesRevealed: gameStatsRef.current.tilesRevealed,
      lastUpdated: new Date().toISOString(), // Will be overwritten by saveInProgressGame
    };

    saveInProgressGame(inProgressData);
  }, [gameNumber, title, gridSize, gameState, log]);

  // Keep ref in sync
  useEffect(() => {
    saveInProgressStateRef.current = saveInProgressState;
  }, [saveInProgressState]);

  // Handle game state updates from GameBoard
  const handleGameStateUpdate = useCallback((revealedCount: number, totalTiles: number) => {
    // log('Game state updated:', { revealedCount, totalTiles });
    const newStats = {
      tilesRevealed: revealedCount,
      totalTiles,
      progress: Math.round((revealedCount / totalTiles) * 100),
      remaining: totalTiles - revealedCount,
    };
    setGameStats(newStats);
    gameStatsRef.current = newStats;

    // Initialize tile tracking on first update
    if (allTileIdsRef.current.length === 0 && totalTiles > 0) {
      const allIds = Array.from({ length: totalTiles }, (_, i) => i);
      allTileIdsRef.current = allIds;
      unflippedTileIdsRef.current = [...allIds];
      log('Initialized tile tracking:', { totalTiles, allIds });

      // Check the local storage to see if the user has played this game before
      const completedGame = getCompletedGame(String(gameNumber));
      if (completedGame) {
        log('User has already completed this game before:', { gameNumber });
        // Trigger gameOver inside GameBoard
        if (triggerGameOverRef.current) {
          setTimeout(() => {
            triggerGameOverRef.current(completedGame.won ? 'won' : 'out-of-guesses');
          }, 824); // Slight delay to allow GameBoard to finish setup
        }
      } else {
        // Check for in-progress game data
        const inProgressGame = getInProgressGame(String(gameNumber));
        if (inProgressGame) {
          log('Found in-progress game data:', inProgressGame);

          // Validate grid size matches
          if (inProgressGame.gridSize !== gridSize) {
            log('WARNING: Grid size mismatch. Saved:', inProgressGame.gridSize, 'Current:', gridSize);
            log('Clearing invalid in-progress data');
            clearInProgressGame(String(gameNumber));
            return;
          }

          // Restore the game state
          setTimeout(() => {
            restoreInProgressGame(inProgressGame);
          }, 300); // Small delay to ensure GameBoard is fully initialized
        }
      }
    }
  }, []);

  // Handle guess tracking from GameBoard
  const handleGuessUsed = useCallback((guessesUsed: number, totalGuesses: number) => {
    // log('Guess used:', { guessesUsed, totalGuesses });
    const newStats = {
      guessesUsed,
      totalGuesses,
      remaining: totalGuesses - guessesUsed,
    };
    setGuessStats(newStats);
    guessStatsRef.current = newStats;
  }, []);

  // Handle guess meter updates from GameBoard
  const handleGuessMeterUpdate = useCallback((guessesUsed: number, incorrectGuesses: number[]) => {
    // Don't update meter if game is over (correct answer submitted)
    if (gameState !== 'playing') return;

    // log('Guess meter update received:', { guessesUsed, incorrectGuesses });
    const newMeterData: Array<'tile' | 'incorrect'> = [];
    for (let i = 1; i <= guessesUsed; i++) {
      if (incorrectGuesses.includes(i)) {
        newMeterData.push('incorrect');
      } else {
        newMeterData.push('tile');
      }
    }
    // log('Generated meter data:', newMeterData);
    setGuessMeterData(newMeterData);
    guessMeterDataRef.current = newMeterData; // Update ref immediately

    // Save immediately for incorrect text guesses
    if (!isRestoringStateRef.current && incorrectGuesses.length > 0) {
      saveInProgressStateRef.current?.();
    }
  }, [gameState]);

  // Handle info modal close
  const handleInfoModalClose = useCallback(() => {
    setShowInfoModal(false);
    // Mark that user has seen the info modal
    setHasSeenInfoModal(true);
  }, []);

  // Handle info modal open
  const handleInfoModalOpen = useCallback(() => {
    setShowInfoModal(true);
  }, []);

  // Handle receiving getRemainingTiles function from GameBoard
  const handleGetRemainingTiles = useCallback((getRemainingTilesCallback: () => number[]) => {
    getRemainingTilesRef.current = getRemainingTilesCallback;
  }, []);

  // Handle receiving flipTile function from GameBoard
  const handleGetFlipTileFunction = useCallback((flipTileCallback: (tileId: number) => void) => {
    flipTileRef.current = flipTileCallback;
  }, []);

  // Handle receiving tiles map getter from GameBoard
  const handleGetTilesMap = useCallback((getTilesMapCallback: () => Map<number, { wrapper: HTMLDivElement }>) => {
    getTilesMapRef.current = getTilesMapCallback;
  }, []);

  // Handle receiving GameInput and GameMeter refs from GameBoard
  const handleGetwrapperGameInputRef = useCallback((ref: HTMLDivElement | null) => {
    wrapperGameInputRef.current = ref;
  }, []);

  // Handle receiving GameMeter ref from GameBoard
  const handleGetwrapperGameMeterRef = useCallback((ref: HTMLDivElement | null) => {
    wrapperGameMeterRef.current = ref;
  }, []);

  // Handle receiving triggerGameOver function from GameBoard
  const handleGetTriggerGameOver = useCallback((triggerGameOverCallback: (reason: 'won' | 'out-of-guesses') => void) => {
    triggerGameOverRef.current = triggerGameOverCallback;
  }, []);

  // Handle tile flipped notification from GameBoard
  const handleTileFlipped = useCallback((tileId: number) => {
    // Remove from unflipped array
    unflippedTileIdsRef.current = unflippedTileIdsRef.current.filter(id => id !== tileId);

    // Save immediately - refs have the current state
    if (!isRestoringStateRef.current) {
      saveInProgressStateRef.current?.();
    }
  }, []);

  // -------------------- Generators

  // Emoji meter generator for sharing (memoized)
  const emojiMeter = useMemo(() => {
    const TOTAL_GUESSES = 10;
    let emojiString = '';

    // Add all guesses from the meter data (tiles and incorrect submissions)
    for (let i = 0; i < guessMeterData.length; i++) {
      if (guessMeterData[i] === 'incorrect') {
        emojiString += EMOJI.INCORRECT_GUESS;
      } else {
        emojiString += EMOJI.TILE_REVEALED;
      }
    }

    // If game was won, add icon for the winning guess (only if not already at max guesses)
    if (gameState === 'won') {
      // Only add winning guess emoji if player hasn't already used all 10 guesses
      if (guessMeterData.length < TOTAL_GUESSES) {
        // Use checkmark if enabled, otherwise use black square (same as unused)
        emojiString += USE_ICON_FOR_CORRECT ? EMOJI.CORRECT_GUESS : EMOJI.UNUSED;
        // Fill remaining with black squares
        const remaining = TOTAL_GUESSES - guessMeterData.length - 1;
        if (remaining > 0) {
          emojiString += EMOJI.UNUSED.repeat(remaining);
        }
      }
      // If already at 10 guesses, don't add extra emoji - they won in exactly 10
    } else {
      // Game lost - fill remaining with black squares
      const remaining = TOTAL_GUESSES - guessMeterData.length;
      if (remaining > 0) {
        emojiString += EMOJI.UNUSED.repeat(remaining);
      }
    }

    return emojiString;
  }, [gameState, guessMeterData]);

  // -------------------- Effects

  // Save completed game to localStorage when game ends
  useEffect(() => {
    if (gameState !== 'won' && gameState !== 'lost') return;
    if (!gameData.gameNumber) return;

    // Don't save if this was already a completed game we're just viewing
    const alreadyCompleted = getCompletedGame(String(gameData.gameNumber));
    if (alreadyCompleted) return;

    const gameProgress: GameProgress = {
      gameNumber: String(gameData.gameNumber),
      emojiSet: emojiMeter,
      completedAt: new Date().toISOString(),
      guessCount: guessStats.guessesUsed,
      won: gameState === 'won',
      tilesRevealed: gameStats.tilesRevealed,
      gameTitle: title,
      guessCountFormatted: `(${guessStats.guessesUsed}/10)`,
    };

    saveCompletedGame(gameProgress);
    log('Saved completed game to localStorage', gameProgress);

    // Note: saveCompletedGame already clears in-progress data
  }, [gameState, gameData.gameNumber, emojiMeter, guessStats.guessesUsed, log, gameStats.tilesRevealed, title]);

  // Initialize Howl for audio if provided
  useEffect(() => {
    fadeOutAndStopAudio(200, true);

    if (gameData.audio) {
      audioHowlRef.current = new Howl({ src: gameData.audio, volume: 0, html5: false, autoplay: false });
    }
  }, [gameData, fadeOutAndStopAudio]);

  // Ensure navigation/unmount fades out and stops any in-flight cover story audio.
  useEffect(() => {
    return () => {
      fadeOutAndStopAudio(300, true);
    };
  }, [fadeOutAndStopAudio]);

  // -------------------- Render

  if (!mounted || !isViewSizeReady) return null;

  return (
    <div className={cx('game', { 'game-over': showGameOver })} ref={rootRef}>

      {/* Game Header */}
      <GameHeader title={title} gameData={gameData} gameState={gameState} />

      {/* Game Main Content: GameBoard, GameOver */}
      <div
        className={cx('game-main-content')}
        ref={gameMainContentRef}
        {...(showInfoModal && { inert: '' as any })}
      >

        {/* 01: Wrapper - Game Board */}
        <div
          className={cx('wrapper-game-board', { 'game-is-over': showGameOver })}
          ref={wrapperGameBoardRef}
        >
          <GameBoard
            key={`${config.imageSrc}-${config.gridSize}`}
            gridSize={config.gridSize}
            canvasSize={config.canvasSize}
            imageSrc={config.imageSrc}
            correctAnswer={albumTitle}
            onGameStateUpdate={handleGameStateUpdate}
            onGuessUsed={handleGuessUsed}
            onGuessMeterUpdate={handleGuessMeterUpdate}
            onGameOver={handleGameOver}
            onGetRemainingTiles={handleGetRemainingTiles}
            onGetFlipTileFunction={handleGetFlipTileFunction}
            onGetTilesMap={handleGetTilesMap}
            onGetwrapperGameInputRef={handleGetwrapperGameInputRef}
            onGetwrapperGameMeterRef={handleGetwrapperGameMeterRef}
            onTileFlipped={handleTileFlipped}
            onTriggerGameOver={handleGetTriggerGameOver}
            initialGuessesUsed={restoredGuessesUsed}
            initialIncorrectGuesses={restoredIncorrectGuesses}
            copy={{
              gameInput: Copy_GameInput,
              gameMeter: Copy_GameMeter,
              finalGuessToast: Copy_FinalGuessToast,
            }}
          />
        </div>

        {/* 02: Wrapper - Game Over */}
        {showGameOver && gameOverCopyData && (
          <div className={cx('wrapper-game-over')} ref={wrapperGameOverRef}>
            <GameOver
              ref={gameOverRef}
              gameResult={gameState === 'won' ? 'won' : 'lost'}
              albumImage={albumImageBase64}
              guessMeterEmoji={emojiMeter}
              gameTitle={title}
              gameNumber={gameData.gameNumber ? String(gameData.gameNumber) : ''}
              guessCount={`(${guessStats.guessesUsed}/10)`}
              copy={gameOverCopyData}
            />
          </div>
        )}

      </div>

      {/* Game Footer */}
      <div
        className={cx('game-footer')}
        ref={gameFooterRef}
        {...(showInfoModal && { inert: '' as any })}
      >

        {/* Game Name */}
        <span className={cx('title-uc-xs', 'game-footer-name')}>
          Cover Story
        </span>

        {/* Help Button */}
        <button className={cx('help-button')}
          onClick={handleInfoModalOpen}
          aria-label={options.ariaLabels?.showInstructions || 'How to play'}
        >
          <IconQuestionMark aria-hidden="true" className={"game-icon"} />
        </button>

      </div>

      {/* Info Modal */}
      <GameInfoModal
        isVisible={showInfoModal}
        onClose={handleInfoModalClose}
        copy={Copy_GameInfoModal}
      />

      {/* Debug Answer */}
      {/* {albumTitle && (
        <span className={cx('debug-answer')}>{albumTitle}</span>
      )} */}

      {/* Debug Button - Clear localStorage, privacy element */}
      {DEBUG_BUTTONS && (
        <>
          <button className={`debug-button ${cx('debug-clear-storage-button')}`}
            onClick={() => {
              clearGameData();
              log('localStorage cleared via debug button');
            }}
          >
            Clear Storage
          </button>

          <div className={cx('debug-fake-privacy-element')}>
            Privacy
          </div>
        </>
      )}

      {/* Cursor Follower (appears when game is over) */}
      <CursorFollower
        propClassName={cx("circle-cta")}
        circleText={Copy_GameOver.circleCta}
        container={rootRef}
        icon={IconArrowDownCta}
        iconClass={cx(['circle-cta-icon'])}
        isVisible={showCursorFollower}
        mouseActive={false}
        externalPos={externalCursorPos}
        onClick={() => {
          // Scroll down to next section
          const gameElement = rootRef.current;
          if (gameElement) {
            const rect = gameElement.getBoundingClientRect();
            const scrollTarget = window.scrollY + rect.bottom;
            window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
          }
        }}
      />

    </div>
  );

};

export default Game;
